import React, { useState, useEffect } from 'react';
import {
  Card, Button, Drawer, Badge, Empty, Spin, Popconfirm, message,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import styled from 'styled-components';
import DictionaryForm from './components/DictionaryForm';
import DictionaryPreview from './components/DictionaryPreview';
import getToken from '../../../../logic/utils/getToken';

const PageContainer = styled.div`
  padding: 24px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
`;

const DictionaryCard = styled(Card)`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  .ant-card-body {
    padding: 20px;
    flex: 1;
  }
`;

const CardTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1890ff;
`;

const CardDescription = styled.div`
  margin-top: 8px;
  color: #666;
  font-size: 13px;
  line-height: 1.5;
`;

const typeLabels = {
  general: { text: 'Общий', color: 'blue' },
  operators: { text: 'Для операторов', color: 'green' },
  queues: { text: 'Для очередей', color: 'orange' },
};

function Dictionaries() {
  const [dictionaries, setDictionaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [currentDictionary, setCurrentDictionary] = useState(null);
  const [previewDictionary, setPreviewDictionary] = useState(null);

  const fetchDictionaries = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/dictionaries');
      if (response.data.status === 'success') {
        setDictionaries(response.data.data);
      }
    } catch (error) {
      message.error('Ошибка при загрузке справочников');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDictionaries();
  }, []);

  const handleCreate = () => {
    setCurrentDictionary(null);
    setFormVisible(true);
  };

  const handleEdit = (dictionary) => {
    setCurrentDictionary(dictionary);
    setFormVisible(true);
  };

  const handlePreview = (dictionary) => {
    setPreviewDictionary(dictionary);
    setPreviewVisible(true);
  };

  const handleDelete = async (id) => {
    const token = getToken();
    try {
      const response = await axios.delete(`/api/dictionaries/${id}`, {
        headers: { token },
      });
      if (response.data.status === 'success') {
        message.success('Справочник успешно удален');
        fetchDictionaries();
      }
    } catch (error) {
      message.error('Ошибка при удалении справочника');
    }
  };

  const handleFormSubmit = async (values) => {
    const token = getToken();
    try {
      if (currentDictionary) {
        const response = await axios.put(
          `/api/dictionaries/${currentDictionary.id}`,
          values,
          { headers: { token } },
        );
        if (response.data.status === 'success') {
          message.success('Справочник успешно обновлен');
        }
      } else {
        const response = await axios.post('/api/dictionaries', values, {
          headers: { token },
        });
        if (response.data.status === 'success') {
          message.success('Справочник успешно создан');
        }
      }
      setFormVisible(false);
      fetchDictionaries();
    } catch (error) {
      message.error('Ошибка при сохранении справочника');
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Справочники</PageTitle>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Создать справочник
        </Button>
      </PageHeader>

      {loading && (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      )}
      {!loading && dictionaries.length === 0 && (
        <Empty description="Нет справочников" />
      )}
      {!loading && dictionaries.length > 0 && (
        <CardsGrid>
          {dictionaries.map((dict) => (
            <Badge.Ribbon
              key={dict.id}
              text={typeLabels[dict.type]?.text || dict.type}
              color={typeLabels[dict.type]?.color || 'default'}
            >
              <DictionaryCard
                actions={[
                  <EyeOutlined key="view" onClick={() => handlePreview(dict)} />,
                  <EditOutlined key="edit" onClick={() => handleEdit(dict)} />,
                  <Popconfirm
                    key="delete"
                    title="Вы уверены, что хотите удалить этот справочник?"
                    onConfirm={() => handleDelete(dict.id)}
                    okText="Да"
                    cancelText="Нет"
                  >
                    <DeleteOutlined />
                  </Popconfirm>,
                ]}
              >
                <CardTitle>{dict.name}</CardTitle>
                {dict.description && (
                  <CardDescription>{dict.description}</CardDescription>
                )}
                <div style={{ marginTop: '12px', color: '#666', fontSize: '12px' }}>
                  Создан:
                  {' '}
                  {new Date(dict.createdAt).toLocaleString('ru-RU')}
                </div>
                {dict.updatedAt !== dict.createdAt && (
                  <div style={{ color: '#666', fontSize: '12px' }}>
                    Обновлен:
                    {' '}
                    {new Date(dict.updatedAt).toLocaleString('ru-RU')}
                  </div>
                )}
              </DictionaryCard>
            </Badge.Ribbon>
          ))}
        </CardsGrid>
      )}

      <Drawer
        title={currentDictionary ? 'Редактировать справочник' : 'Создать справочник'}
        open={formVisible}
        onClose={() => setFormVisible(false)}
        width={720}
        destroyOnClose
      >
        <DictionaryForm
          dictionary={currentDictionary}
          onSubmit={handleFormSubmit}
          onCancel={() => setFormVisible(false)}
        />
      </Drawer>

      <Drawer
        title="Предпросмотр справочника"
        open={previewVisible}
        onClose={() => setPreviewVisible(false)}
        width={600}
      >
        <DictionaryPreview dictionary={previewDictionary} />
      </Drawer>
    </PageContainer>
  );
}

export default Dictionaries;
